import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { authReady } from '@shared/firebase/init';
import type { User } from '@shared/types';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => {
  // Wait for persistence to be ready, then listen for auth state changes
  authReady.then(() => {
    onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.exists()
            ? ({ id: userDoc.id, ...userDoc.data() } as User)
            : null;
          set({
            firebaseUser,
            user: userData,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            firebaseUser,
            user: null,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } else {
        set({
          firebaseUser: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    });
  });

  return {
    firebaseUser: null,
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,

    login: async (email, password) => {
      set({ isLoading: true, error: null });
      try {
        await authReady;
        await signInWithEmailAndPassword(auth, email, password);
        // onAuthStateChanged will handle setting isAuthenticated
      } catch (err: any) {
        set({ isLoading: false, error: err.message });
      }
    },

    register: async (email, password, displayName) => {
      set({ isLoading: true, error: null });
      try {
        await authReady;
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        // These are nice-to-have, don't block auth if they fail
        try {
          await updateProfile(cred.user, { displayName });
          await setDoc(doc(db, 'users', cred.user.uid), {
            email: cred.user.email,
            displayName,
            createdAt: new Date(),
          });
        } catch {
          // Profile/doc creation failed but user is still authenticated
          console.warn('User created but profile setup failed — will retry later');
        }
        // onAuthStateChanged will handle setting isAuthenticated
      } catch (err: any) {
        set({ isLoading: false, error: err.message });
      }
    },

    logout: async () => {
      await signOut(auth);
    },

    clearError: () => set({ error: null }),
  };
});
